"use client";

/**
 * Safe mode — crash-loop recovery for devices where the full app kills the
 * browser renderer (iOS "A problem repeatedly occurred").
 *
 * The inline <head> script in app/layout.tsx detects two consecutive hard
 * deaths (boot without a prior clean-exit flag) and sets
 * `<html data-bb-safe="1">` BEFORE the app bundle loads. Components read
 * that flag through useSafeMode() and skip their heaviest work: hero media,
 * most home sections, visualizers, decorative layers.
 *
 * The flag is per-boot; the crash counter resets automatically after any
 * clean exit, or immediately via exitSafeMode() (the banner button).
 */

import { useEffect, useState } from "react";

export function isSafeModeNow(): boolean {
  try {
    return typeof document !== "undefined"
      && document.documentElement.getAttribute("data-bb-safe") === "1";
  } catch {
    return false;
  }
}

/**
 * Hydration-safe hook: always false on the server AND on the client's first
 * render, then reflects the real flag post-mount. Safe-mode reductions all
 * apply to client-side, post-data-fetch rendering, so the deferred flip
 * still prevents the heavy work.
 */
export function useSafeMode(): boolean {
  const [safe, setSafe] = useState(false);
  useEffect(() => { setSafe(isSafeModeNow()); }, []);
  return safe;
}

/** Reset the crash counter and reload one render-tier richer (banner
 *  button). Clears the tier ceiling — an explicit user retry re-tests
 *  upward; a new crash simply re-establishes the ceiling one lower. */
export function exitSafeMode(): void {
  try {
    localStorage.setItem("bb-crash-count", "0");
    localStorage.setItem("bb-clean-exit", "1");
    const cur = parseInt(localStorage.getItem("bb-tier") || "3", 10);
    const next = Math.min(3, (isNaN(cur) ? 3 : cur) + 1);
    localStorage.setItem("bb-tier", String(next));
    localStorage.setItem("bb-tier-ceil", "4");
  } catch { /* ignore */ }
  try { (window as any).__bbSend?.("safe-mode-exit", {}); } catch { /* ignore */ }
  window.location.reload();
}

/**
 * Self-bisecting render tier, set on `<html data-bb-tier>` by the inline
 * boot script in app/layout.tsx BEFORE the bundle loads.
 *   0 = minimal (static hero, 3 sections, 6-card rows)
 *   1 = stability step (no extra features — VU strips were exonerated by
 *       bisection data and now render at every tier outside safe mode)
 *   2 = +hero slideshow · 3 = full
 * iPhones start at 0 and climb one tier per stable session; a hard death
 * at tier T sets a persistent ceiling so the device settles at T-1.
 * Other platforms boot straight at 3.
 */
export function deviceTierNow(): number {
  try {
    if (typeof document === "undefined") return 3;
    const t = parseInt(document.documentElement.getAttribute("data-bb-tier") || "3", 10);
    return isNaN(t) ? 3 : Math.max(0, Math.min(3, t));
  } catch {
    return 3;
  }
}

/**
 * Hydration-safe hook: full tier (3) on the server AND the client's first
 * render, then the real tier post-mount — same deferred-flip pattern as
 * useSafeMode; all tier-gated work is post-data-fetch so nothing heavy
 * runs before the flip.
 */
export function useDeviceTier(): number {
  const [tier, setTier] = useState(3);
  useEffect(() => { setTier(deviceTierNow()); }, []);
  return tier;
}
